import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, Send, TerminalSquare, Monitor, Copy, Trash2, RefreshCw } from "lucide-react";
import { StatusBadge, KPI } from "@/components/Primitives";

export default function Agents() {
  const [status, setStatus] = useState(null);
  const [agents, setAgents] = useState([]);
  const [actions, setActions] = useState([]);
  const [clients, setClients] = useState([]);
  const [query, setQuery] = useState("");
  const [pickedAgent, setPickedAgent] = useState("");
  const [token, setToken] = useState(null);
  const [meta, setMeta] = useState(null);
  const [running, setRunning] = useState(false);

  const [action, setAction] = useState({
    kind: "powershell",
    command: "hostname",
  });

  const load = async () => {
    const [s, a, ac, c] = await Promise.all([
      api.get("/agent/status"),
      api.get("/agents"),
      api.get("/agent/actions"),
      api.get("/clients"),
    ]);
    setStatus(s.data);
    setAgents(a.data);
    setActions(ac.data);
    setClients(c.data);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const filteredAgents = useMemo(() => {
    const q = query.toLowerCase();
    return agents.filter((a) =>
      (a.hostname || "").toLowerCase().includes(q) ||
      (a.id || "").toLowerCase().includes(q) ||
      (a.os || "").toLowerCase().includes(q) ||
      (a.metrics?.ip_address || "").toLowerCase().includes(q) ||
      (a.metrics?.current_user || "").toLowerCase().includes(q)
    );
  }, [agents, query]);

  const selectedAgent = agents.find((a) => a.id === pickedAgent) || filteredAgents[0] || null;
  const m = selectedAgent?.metrics || {};
  const h = m.hardware || {};

  const selectedActions = actions
    .filter((a) => a.agent_id === selectedAgent?.id)
    .slice(0, 25);

  const assignClient = async (clientId) => {
    if (!selectedAgent) return;
    try {
      await api.patch(`/agents/${selectedAgent.id}`, { client_id: clientId || null });
      toast.success(clientId ? "Agent assigned to client" : "Agent unassigned");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Failed to assign client");
    }
  };

  const getToken = async () => {
    const { data } = await api.post("/agents/install-token");
    setToken(data.install_token);
    setMeta(data);
    toast.success("Install token generated");
  };

  const pushAction = async () => {
    const target = pickedAgent || selectedAgent?.id;
    if (!target) return toast.error("Select an agent first");
    if (!action.command.trim()) return toast.error("Command is required");

    try {
      setRunning(true);
      const r = await api.post(`/agent/${target}/action`, action);
      toast.success(`Command ${r.data.delivered ? "delivered live" : "queued"}`);
      setPickedAgent(target);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Command failed");
    } finally {
      setRunning(false);
    }
  };

  const removeOfflineAgent = async (agent) => {
    if (!agent) return;
    if (agent.status === "online") return toast.error("Cannot remove an online agent.");

    if (!window.confirm(`Remove offline agent ${agent.hostname || agent.id}?`)) return;

    await api.delete(`/agent/${agent.id}`);
    toast.success("Offline agent removed");
    if (pickedAgent === agent.id) setPickedAgent("");
    load();
  };

  const quickCommands = [
    ["Hostname", "hostname"],
    ["System Info", "Get-ComputerInfo | Select-Object CsName, WindowsProductName, OsVersion"],
    ["IP Config", "ipconfig /all"],
    ["Services", "Get-Service | Select-Object -First 30 Name, Status, DisplayName"],
    ["Processes", "Get-Process | Select-Object -First 25 ProcessName, Id, CPU"],
    ["Disk", "Get-PSDrive -PSProvider FileSystem"],
    ["Users", "query user"],
    ["Spooler", "Get-Service Spooler"],
    ["Restart Spooler", "Restart-Service Spooler -Force; Get-Service Spooler"],
    ["Reboot", "Restart-Computer -Force"],
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">APOCALYNX Agent</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Remote Shell Command Center</h1>
          <p className="text-sm text-zinc-500 mt-1">Search endpoints, inspect telemetry, and execute remote PowerShell/CMD commands.</p>
        </div>

        <button onClick={load} className="bg-zinc-900 border border-zinc-800 hover:border-blue-700 rounded px-3 py-2 text-sm flex items-center gap-2">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {status && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KPI label="Live Connections" value={status.live_connections} tone="good" />
          <KPI label="Online" value={status.online} tone="good" />
          <KPI label="Offline" value={status.offline} />
          <KPI label="Stale (5m)" value={status.stale_5m} tone={status.stale_5m > 0 ? "warn" : "default"} />
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        <div className="xl:col-span-4 bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
          <div className="p-4 border-b border-zinc-900">
            <div className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded px-3 py-2">
              <Search size={15} className="text-zinc-500" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search hostname, user, IP, OS..."
                className="bg-transparent outline-none text-sm w-full"
              />
            </div>
          </div>

          <div className="max-h-[700px] overflow-auto">
            {filteredAgents.map((a) => (
              <button
                key={a.id}
                onClick={() => setPickedAgent(a.id)}
                className={`w-full text-left px-4 py-3 border-b border-zinc-900 hover:bg-zinc-900/50 ${
                  selectedAgent?.id === a.id ? "bg-blue-950/30" : ""
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="font-mono text-sm">{a.hostname || a.id}</div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={a.status} />
                    {a.status !== "online" && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          removeOfflineAgent(a);
                        }}
                        className="inline-flex border border-red-900/60 bg-red-950/40 hover:bg-red-900/60 text-red-300 rounded px-2 py-1"
                      >
                        <Trash2 size={12} />
                      </span>
                    )}
                  </div>
                </div>

                <div className="text-xs text-zinc-500 mt-1 truncate">{a.os}</div>
                <div className="grid grid-cols-3 gap-2 mt-2 text-[11px] font-mono text-zinc-400">
                  <span>CPU {a.metrics?.cpu ?? "—"}%</span>
                  <span>MEM {a.metrics?.mem ?? "—"}%</span>
                  <span>DISK {a.metrics?.disk ?? "—"}%</span>
                </div>
              </button>
            ))}

            {filteredAgents.length === 0 && (
              <div className="px-4 py-8 text-sm text-zinc-500">No matching agents.</div>
            )}
          </div>
        </div>

        <div className="xl:col-span-8 space-y-6">
          <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
            <div className="flex items-center gap-2 mb-4">
              <Monitor size={16} className="text-blue-400" />
              <div className="font-display font-medium">
                {selectedAgent ? selectedAgent.hostname || selectedAgent.id : "No agent selected"}
              </div>
            </div>

            {selectedAgent && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Info label="User" value={m.current_user || h.current_user} />
                <Info label="IP" value={m.ip_address || h.ip_address} />
                <Info label="OS" value={h.os_caption || selectedAgent.os} />
                <Info label="Agent" value={m.agent_version || selectedAgent.version} />
                <Info label="Manufacturer" value={h.manufacturer || selectedAgent.manufacturer} />
                <Info label="Model" value={h.model || selectedAgent.model} />
                <Info label="Serial" value={h.serial_number || selectedAgent.serial_number} />
                <Info label="Last Heartbeat" value={selectedAgent.last_heartbeat?.slice(0, 19)?.replace("T", " ")} />
              </div>
            )}

            {selectedAgent && (
              <div className="mt-4 bg-zinc-900/40 border border-zinc-900 rounded p-3">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">Assigned Client</div>
                <select
                  value={selectedAgent.client_id || ""}
                  onChange={(e) => assignClient(e.target.value)}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm"
                >
                  <option value="">Unassigned / MSP internal</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="bg-zinc-950 border border-blue-900/50 rounded-md overflow-hidden">
            <div className="px-5 py-3 border-b border-zinc-900 flex items-center justify-between">
              <div className="font-display font-medium flex items-center gap-2">
                <TerminalSquare size={16} className="text-blue-400" />
                Remote Shell
              </div>
              <div className="text-xs text-zinc-500 font-mono">
                {selectedAgent?.status === "online" ? "LIVE SESSION READY" : "OFFLINE / QUEUED MODE"}
              </div>
            </div>

            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                <select
                  value={action.kind}
                  onChange={(e) => setAction({ ...action, kind: e.target.value })}
                  className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm"
                >
                  <option>powershell</option>
                  <option>cmd</option>
                </select>

                <input
                  value={action.command}
                  onChange={(e) => setAction({ ...action, command: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") pushAction();
                  }}
                  className="md:col-span-4 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm font-mono"
                  placeholder="Enter command..."
                />
              </div>

              <div className="flex flex-wrap gap-2">
                {quickCommands.map(([label, cmd]) => (
                  <button
                    key={label}
                    onClick={() => setAction({ kind: "powershell", command: cmd })}
                    className="text-xs bg-zinc-900 border border-zinc-800 hover:border-blue-700 rounded px-3 py-1.5 text-zinc-300"
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

                <button
                  onClick={async () => {
                    if (!selectedAgent) {
                      toast.error("Select an agent first");
                      return;
                    }

                    const consentScript = `
Add-Type -AssemblyName PresentationFramework

$result = [System.Windows.MessageBox]::Show(
"APOCALYNX Support is requesting a remote support session. Click OK to launch RustDesk and share the temporary session details with support.",
"Remote Support Request",
"OKCancel",
"Information"
)

if ($result -ne "OK") {
    Write-Output "REMOTE SESSION DECLINED BY USER"
    exit 0
}

$paths = @(
    "$env:ProgramFiles\\RustDesk\\rustdesk.exe",
    "$env:ProgramFiles(x86)\\RustDesk\\rustdesk.exe",
    "$env:LOCALAPPDATA\\Programs\\RustDesk\\rustdesk.exe"
)

$RustDesk = $null
foreach ($candidate in $paths) {
    if (Test-Path $candidate) {
        $RustDesk = $candidate
        break
    }
}

if (-not $RustDesk) {
    Write-Output "RUSTDESK_NOT_FOUND"
    Write-Output "Opening download page for user..."
    Start-Process "https://rustdesk.com/download"
    exit 1
}

$TempPassword = "APX-" + (-join ((65..90) + (48..57) | Get-Random -Count 10 | ForEach-Object {[char]$_}))

try {
    & $RustDesk --password $TempPassword | Out-Null
    Start-Sleep -Seconds 2
} catch {
    Write-Output "PASSWORD_SET_ERROR: $($_.Exception.Message)"
}

$RustDeskId = ""
try {
    $RustDeskId = (& $RustDesk --get-id) | Out-String
    $RustDeskId = $RustDeskId.Trim()
} catch {
    Write-Output "GET_ID_ERROR: $($_.Exception.Message)"
}

Start-Process $RustDesk

Write-Output "REMOTE SESSION APPROVED BY USER"
Write-Output "RUSTDESK_PATH=$RustDesk"
Write-Output "RUSTDESK_ID=$RustDeskId"
Write-Output "TEMP_PASSWORD=$TempPassword"
Write-Output "NOTE=Password is temporary for this support session. Rotate/clear after use if required."
`;

                    try {
                      await api.post(
                        `/agent/${selectedAgent.id}/action`,
                        {
                          kind: "powershell",
                          command: consentScript
                        }
                      );

                      toast.success("Remote session request sent");
                      load();
                    } catch (err) {
                      toast.error("Failed to send remote session request");
                    }
                  }}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded px-3 py-2"
                >
                  Request Remote Session
                </button>

                <button
                  onClick={async () => {
                    if (!selectedAgent) {
                      toast.error("Select an agent first");
                      return;
                    }

                    try {
                      await api.post(
                        `/agent/${selectedAgent.id}/action`,
                        {
                          kind: "powershell",
                          command: "winget install RustDesk.RustDesk -e --silent"
                        }
                      );

                      toast.success("RustDesk installation command sent");
                      load();
                    } catch (err) {
                      toast.error("Failed to install RustDesk");
                    }
                  }}
                  className="bg-amber-600 hover:bg-amber-700 text-white text-sm rounded px-3 py-2"
                >
                  Install RustDesk
                </button>

              </div>

              <button
                onClick={pushAction}
                disabled={!selectedAgent || running}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-800 disabled:text-zinc-500 text-white text-sm rounded px-3 py-2 flex items-center justify-center gap-2"
              >
                <Send size={15} />
                {running ? "Sending..." : `Execute on ${selectedAgent?.hostname || "selected agent"}`}
              </button>
            </div>
          </div>

          <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
            <div className="px-5 py-3 border-b border-zinc-900 font-display font-medium">
              Remote Shell Output
            </div>

            <div className="max-h-[520px] overflow-auto bg-black">
              {selectedActions.map((a) => (
                <div key={a.id} className="border-b border-zinc-900 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-mono text-xs text-blue-300">
                      {a.kind} &gt; {a.command}
                    </div>
                    <span className={a.status === "ok" ? "text-emerald-400 text-xs" : a.status === "error" ? "text-red-400 text-xs" : "text-amber-400 text-xs"}>
                      {a.status || "pending"}
                    </span>
                  </div>

                  <pre className="mt-3 bg-zinc-950 border border-zinc-900 rounded p-3 text-[11px] font-mono text-zinc-300 whitespace-pre-wrap overflow-auto max-h-64">
                    {a.output || "No output yet. Wait for next agent heartbeat/result refresh."}
                  </pre>
                </div>
              ))}

              {selectedActions.length === 0 && (
                <div className="px-5 py-8 text-sm text-zinc-500">
                  No remote commands executed for this agent yet.
                </div>
              )}
            </div>
          </div>

          <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
            <div className="font-display font-medium mb-3">Inventory Snapshot</div>
            <pre className="bg-zinc-900/40 border border-zinc-900 rounded p-3 text-[11px] font-mono text-zinc-300 whitespace-pre-wrap overflow-auto max-h-80">
              {JSON.stringify(
                {
                  hardware: m.hardware,
                  disks: m.disks,
                  network: m.network,
                  security: m.security,
                  software_sample_count: m.software_sample?.length || 0,
                  startup_programs_count: m.startup_programs?.length || 0,
                },
                null,
                2
              )}
            </pre>
          </div>
        </div>
      </div>

      <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="font-display font-medium">Deployment Token</div>
            <div className="text-sm text-zinc-500 mt-1">Generate a token only when installing a new endpoint.</div>
          </div>
          <button onClick={getToken} className="bg-blue-600 hover:bg-blue-700 text-white text-sm rounded px-3 py-2">
            Generate install token
          </button>
        </div>

        {meta && (
          <div className="mt-4 space-y-3">
            <pre className="text-[11px] font-mono bg-zinc-900/40 border border-zinc-900 rounded p-3 break-all whitespace-pre-wrap">
              {token}
            </pre>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <a href="/downloads/APOCALYNXAgent.exe" className="bg-zinc-900 border border-zinc-800 hover:border-blue-700 rounded px-3 py-2 text-sm text-zinc-200 text-center">
                Download APOCALYNXAgent.exe
              </a>
              <a href="/downloads/install-apocalynx-agent.ps1" className="bg-zinc-900 border border-zinc-800 hover:border-blue-700 rounded px-3 py-2 text-sm text-zinc-200 text-center">
                Download Installer Script
              </a>
            </div>

            <div className="flex items-center gap-2">
              <code className="flex-1 bg-zinc-900/40 border border-zinc-900 rounded p-2 font-mono text-[12px] break-all">
                iwr https://rmm.lynxclub.ph/downloads/install-apocalynx-agent.ps1 -OutFile $env:TEMP\install-apocalynx-agent.ps1; powershell -ExecutionPolicy Bypass -File $env:TEMP\install-apocalynx-agent.ps1 -InstallToken "{token}"
              </code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`iwr https://rmm.lynxclub.ph/downloads/install-apocalynx-agent.ps1 -OutFile $env:TEMP\\install-apocalynx-agent.ps1; powershell -ExecutionPolicy Bypass -File $env:TEMP\\install-apocalynx-agent.ps1 -InstallToken "${token}"`);
                  toast.success("Install command copied");
                }}
                className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-zinc-300"
              >
                <Copy size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="bg-zinc-900/40 border border-zinc-900 rounded p-3">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{label}</div>
      <div className="text-sm font-mono text-zinc-100 mt-1 break-all">{value || "—"}</div>
    </div>
  );
}
